import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Music, Snowflake, Sun, AlertCircle, Instagram, Facebook } from "lucide-react";

const BANNERS = [];

const HERO_IMAGES = [
  {
    url: "/logo.png",
    alt: "Logo L'Ancienne Poste",
    isLogo: true,
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/fngvqyte_IMG_0427.jpeg",
    alt: "Christelle et Clément, vos hôtes à L'Ancienne Poste",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/al2orlmy_IMG_0408.jpeg",
    alt: "Façade du restaurant L'Ancienne Poste à Entrechaux",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/rb1n0zvx_IMG_0412.jpeg",
    alt: "La salle du restaurant et son bar en bois",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/e4jwa73m_IMG_0413.jpeg",
    alt: "La salle du restaurant et ses fauteuils rouges",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/0xqjcjqp_IMG_0404.jpeg",
    alt: "La grande salle du restaurant, vue d'ensemble",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/qg7r3i5s_IMG_0400.jpeg",
    alt: "La véranda du restaurant et sa cheminée",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/idcrmdsf_IMG_0397.jpeg",
    alt: "La terrasse ombragée sous la pergola et les guirlandes",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/hrp41twy_IMG_0394.jpeg",
    alt: "La grande terrasse du restaurant sous la pergola",
  },
  {
    url: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/p4tyzbr7_IMG_0405.jpeg",
    alt: "Vue sur le Mont Ventoux depuis la terrasse",
  },
];

export const HeroSection = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [bannerIndex, setBannerIndex] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    const timer = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % BANNERS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const scrollToMenu = () => {
    const el = document.querySelector("#carte");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative min-h-screen flex items-end pb-24 md:pb-32 overflow-hidden"
    >
      {/* Background Images with crossfade */}
      {HERO_IMAGES.map((img, i) => (
        img.isLogo ? (
          <div
            key={img.url}
            className="absolute inset-0 flex items-center justify-center"
            style={{
              backgroundColor: "#000000",
              opacity: i === currentIndex ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
              zIndex: i === currentIndex ? 1 : 0,
            }}
            aria-hidden={i !== currentIndex}
          >
            <img src={img.url} alt={img.alt} className="max-h-[85vh] w-auto object-contain" />
          </div>
        ) : (
          <div
            key={img.url}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${img.url})`,
              backgroundSize: img.contain ? "contain" : "cover",
              backgroundRepeat: "no-repeat",
              backgroundColor: img.contain ? "#2C2420" : undefined,
              opacity: i === currentIndex ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
              zIndex: i === currentIndex ? 1 : 0,
            }}
            aria-hidden={i !== currentIndex}
          />
        )
      ))}
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 z-[2]" />

      {/* Banner - compact pill near top */}
      <div
        className="absolute top-20 sm:top-24 md:top-24 left-1/2 -translate-x-1/2 z-10 w-auto max-w-[92%]"
      >
        {/* Exceptional closure notice — auto-hides after Nov 17, 2026 */}
        {new Date() <= new Date("2026-11-17T23:59:59") && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-3 flex justify-center"
            data-testid="closure-notice"
          >
            <div
              className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-[#7A2E22]/90 backdrop-blur-md
                         rounded-2xl sm:rounded-full shadow-lg border border-white/40 max-w-[92%]"
              style={{ textShadow: "0 1px 6px rgba(0,0,0,0.4)" }}
            >
              <AlertCircle size={16} className="text-white flex-shrink-0" />
              <p
                className="text-xs sm:text-sm text-white text-center"
                style={{ fontFamily: "'Lato', sans-serif", fontWeight: 600 }}
              >
                {t("hero.closure_notice")}
              </p>
            </div>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {BANNERS.map(({ id, Icon }, i) =>
            i === bannerIndex ? (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.6 }}
                data-testid={`hero-banner-${id}`}
                className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-5 px-5 py-3 sm:px-7 sm:py-3.5
                           bg-[#C1624D]/40 backdrop-blur-md rounded-3xl sm:rounded-full shadow-xl border border-white/50 text-center"
                style={{ textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Icon size={16} className="text-white flex-shrink-0 sm:w-5 sm:h-5 drop-shadow-lg" />
                  <p
                    className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white"
                    style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700 }}
                  >
                    {t(`hero.${id}_label`)}
                  </p>
                </div>
                <span className="hidden sm:block w-px h-5 sm:h-6 bg-white/50 flex-shrink-0" />
                <p
                  className="text-base sm:text-xl md:text-2xl text-white italic"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                >
                  {t(`hero.${id}_title`)} <span className="font-normal not-italic text-sm sm:text-base">· {t(`hero.${id}_subtitle`)}</span>
                </p>
              </motion.div>
            ) : null
          )}
        </AnimatePresence>

        {/* Amenities badges */}
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-3 flex flex-wrap justify-center gap-2"
          data-testid="hero-amenities"
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-black/35 backdrop-blur-md rounded-full
                       border border-white/30 text-xs sm:text-sm text-white"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400, textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
          >
            <Snowflake size={13} className="text-[#A5C4D4] flex-shrink-0" />
            {t("reservation.amenities_clim")}
          </span>
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-black/35 backdrop-blur-md rounded-full
                       border border-white/30 text-xs sm:text-sm text-white"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400, textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
          >
            <Sun size={13} className="text-[#F5C97B] flex-shrink-0" />
            {t("reservation.amenities_terrasse")}
          </span>
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10 px-6 md:px-12 max-w-7xl mx-auto w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-sm md:text-base uppercase tracking-[0.3em] text-white/70 mb-4"
          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
        >
          {t("hero.tagline")}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="text-5xl sm:text-6xl lg:text-8xl font-light italic text-white leading-[0.95] mb-6"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          data-testid="hero-title"
        >
          L'Ancienne<br />Poste
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-base md:text-lg text-white/80 max-w-md leading-relaxed mb-10"
          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="flex items-center gap-4 flex-wrap"
        >
          <button
            onClick={scrollToMenu}
            className="group px-8 py-3 rounded-full bg-[#C1624D] text-white font-serif italic text-lg
                       hover:bg-[#A6503C] active:scale-95 transition-all duration-300 shadow-lg hover:shadow-xl"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="hero-cta-button"
          >
            {t("hero.cta")}
          </button>
          <a
            href="https://www.instagram.com/ancienne_poste_entrechaux/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full border-2 border-white/60 backdrop-blur-sm flex items-center justify-center
                       text-white hover:bg-white hover:text-[#2C2420] hover:scale-110 active:scale-95 transition-all duration-300"
            data-testid="hero-instagram-btn"
            aria-label="Compte Instagram"
          >
            <Instagram size={20} />
          </a>
          <a
            href="https://www.facebook.com/share/1CXw9n31Dz/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full border-2 border-white/60 backdrop-blur-sm flex items-center justify-center
                       text-white hover:bg-white hover:text-[#2C2420] hover:scale-110 active:scale-95 transition-all duration-300"
            data-testid="hero-facebook-btn"
            aria-label="Page Facebook"
          >
            <Facebook size={20} />
          </a>
        </motion.div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {HERO_IMAGES.map((img, i) => (
          <button
            key={img.url}
            onClick={() => setCurrentIndex(i)}
            className="w-2 h-2 rounded-full transition-all duration-500"
            style={{
              backgroundColor: i === currentIndex ? "#C1624D" : "rgba(255,255,255,0.4)",
              transform: i === currentIndex ? "scale(1.3)" : "scale(1)",
            }}
            data-testid={`hero-dot-${i}`}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={28} className="text-white/50" />
        </motion.div>
      </motion.div>
    </section>
  );
};
